/**
 * Shared action-button handler for NotificationPopup variants. Maps a
 * NotificationAction's `type` (tokens / plans / chat / image / video / url)
 * to a navigate or window.open call. Extracted so all three template
 * variants (Simple, Showcase, LongForm) handle actions identically.
 */

import { useCallback, useContext, createContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { NotificationAction } from '../../../types';
import { pickLocalized } from '../../../utils/localized';
import type { SupportedLanguage } from '../../../utils/localized';

/** Optional override for the language used by NotificationPopup variants.
 *  Only set by the admin Preview — production rendering reads i18n directly.
 *  When the context value is null, useNotificationLanguage falls back to
 *  i18next. */
export const NotificationLanguageOverrideContext = createContext<SupportedLanguage | null>(null);

export const useNotificationActionHandler = (onDismiss: () => void) => {
  const navigate = useNavigate();

  return useCallback((action: NotificationAction) => {
    onDismiss();

    switch (action.type) {
      case 'tokens':
        navigate('/account?screen=usage');
        break;
      case 'plans':
        navigate('/account/subscription');
        break;
      case 'chat':
        navigate('/chat');
        break;
      case 'image':
        navigate('/image');
        break;
      case 'video':
        navigate('/video');
        break;
      case 'url':
        if (action.url) {
          window.open(action.url, '_blank', 'noopener,noreferrer');
        }
        break;
    }
  }, [navigate, onDismiss]);
};

/** Backwards-compat shim — historically the Simple popup prefixed each
 *  action label with an emoji per type. New designs are emoji-free, so this
 *  always returns an empty string. Kept to avoid touching every callsite at
 *  once; safe to remove once Simple popup is retired. */
export const actionEmoji = (_type: NotificationAction['type']): string => '';

/** Returns the current i18n language narrowed to one of the three
 *  supported by in-app broadcasts. Falls back to 'ru' for unknown locales.
 *  When wrapped in <NotificationLanguageOverrideContext.Provider value=...>,
 *  the provided value wins — used by the admin Preview to swap languages
 *  without touching global i18n. */
export const useNotificationLanguage = (): SupportedLanguage => {
  const override = useContext(NotificationLanguageOverrideContext);
  const { i18n } = useTranslation();
  if (override) return override;
  const lang = i18n.language?.split('-')[0]?.toLowerCase();
  if (lang === 'en' || lang === 'kk') return lang;
  return 'ru';
};

/** Resolve a NotificationAction's label for the given language. Wraps
 *  pickLocalized so callers don't import it just for action buttons. */
export const resolveActionLabel = (
  action: NotificationAction,
  lang: SupportedLanguage,
): string => pickLocalized(action.label, lang);

/** Parse a URL field that may be a single string or a JSON-encoded array
 *  (the convention used by `imageUrl` / `videoUrl` in the notifications
 *  table). Returns a normalised array. */
export const parseMediaUrls = (single?: string, multi?: string[]): string[] => {
  if (multi && multi.length > 0) return multi;
  if (!single) return [];
  if (single.startsWith('[')) {
    try {
      const parsed = JSON.parse(single);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      /* fallthrough to single URL */
    }
  }
  return [single];
};
